import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useToast } from './Toast';
import { useSocket } from '../contexts/SocketContext';
import { formatBJ } from '../utils/datetime';
import { useBeijingToday } from '../utils/useBeijingToday';
import { useAuth } from '../contexts/AuthContext';
import { ImageViewer, StatusBadge } from './PuzzleImageViewer';
import PuzzleFixModal from './PuzzleFixModal';
import { Images, Puzzle, ChevronRight, AlertTriangle } from 'lucide-react';

// 前台 · 证券工作台底部的“图片管理”（和排五工作台底部的“图片管理”同一位置、同一个作用）：
//   今日：今天提交的记录；全部：所有提交过的记录（分页）
//   每条记录点开可看 最终图、字段图、主图、客户对话图、客户资料图；已作废 / 待核实的也在里面，状态一眼能看到
// 没有导出：前台用户不提供导出功能，导出只在后台（管理员 / 已授权的代理商）。
// 编辑：识别成功的记录不提供任何编辑。只有“识别失败、字段留空”的记录（服务端给 user_fix_fields）会多出一条橙色提示 +“去补全”，
//   补全后自动重新生成最终图；最终图没生成出来的（user_fix_regen）显示“重新生成”。电话不能改，补错找管理员 / 代理商在后台更正。
// 没有跳转：底部不放“去证券识别记录”的链接（识别记录入口只在“我的”页）。
// 只看本人的记录（服务端按登录人限定范围）。
const PAGE_SIZE = 20;
const RANGES = [
  { key: 'today', label: '今日' },
  { key: 'all', label: '全部' },
];

export default function PuzzleImagesMine({ refreshKey }) {
  const { showToast } = useToast();
  const { user } = useAuth();
  const { subscribe } = useSocket();
  const today = useBeijingToday();      // 过了 0 点自动变成新的一天，“今日”列表随之切换
  const [range, setRange] = useState('today');
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [fixing, setFixing] = useState(null);      // 正在补全的记录

  const load = useCallback(async (pg, append) => {
    setLoading(true);
    try {
      const res = await api.get('/puzzle/records', {
        params: { status: 'all', page: pg, pageSize: PAGE_SIZE, ...(range === 'today' ? { cdate: today } : {}) },
      });
      setList(prev => (append ? [...prev, ...res.data.list] : res.data.list));
      setTotal(res.data.total || 0);
      setPage(pg);
    } catch { /* 失败保留旧数据 */ }
    finally { setLoading(false); }
  }, [range, today]);

  useEffect(() => { load(1, false); }, [load, refreshKey]);
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => { if (event === 'puzzle_updated') load(1, false); });
  }, [subscribe, load]);

  // 补全 / 重新生成保存成功：就地更新这条记录（不整页重载，列表位置不跳）
  const onFixed = (rec, composeOk) => {
    setList(prev => prev.map(x => (x.id === rec.id ? { ...x, ...rec } : x)));
    setFixing(null);
    showToast(composeOk ? '已保存，最终图已重新生成' : '已保存，但最终图暂时没生成成功，请点“重新生成”，多次失败请联系管理员', composeOk ? 'success' : 'error');
  };
  const closeFix = (needRefresh) => { setFixing(null); if (needRefresh) load(1, false); };

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Images size={18} color="var(--c-primary)" />
        <span style={{ fontSize: 15, fontWeight: 700, flex: 1 }}>图片管理</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {RANGES.map(r => (
            <button key={r.key} onClick={() => setRange(r.key)} style={{
              padding: '3px 12px', borderRadius: 14, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: `1.5px solid ${range === r.key ? 'var(--c-primary)' : 'var(--c-border)'}`,
              background: range === r.key ? 'var(--c-primary)' : 'var(--c-surface)',
              color: range === r.key ? '#fff' : 'var(--c-text-secondary)',
            }}>{r.label}</button>
          ))}
        </div>
      </div>

      {list.length === 0 && !loading && (
        <p style={{ fontSize: 13, color: 'var(--c-text-tertiary)', textAlign: 'center', padding: '12px 0 4px' }}>
          {range === 'today' ? '今日暂无提交记录' : '暂无提交记录'}
        </p>
      )}

      {list.map(r => {
        const thumb = r.images?.combined || r.images?.chat;
        // 只有本人（非管理员）、且服务端认定“识别失败还有空字段 / 最终图缺失”的记录才出现补全提示；识别成功的记录没有任何编辑入口
        const mine = user?.id === r.user_id && user?.role !== 'admin';
        const fixFields = mine ? (r.user_fix_fields || []) : [];
        const needRegen = mine && !!r.user_fix_regen;
        const canFix = fixFields.length > 0 || needRegen;
        const FIELD_NAME = { wechat_id: '微信号', nickname: '昵称', operator_wx: '操作微信号' };
        return (
          <div key={r.id} style={{ borderTop: '0.5px solid var(--c-border)' }}>
            <div onClick={() => setViewing(r)} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '9px 0', cursor: 'pointer' }}>
              <div style={{
                width: 56, height: 56, borderRadius: 10, overflow: 'hidden', flexShrink: 0, background: 'var(--c-bg)',
                border: '1px solid var(--c-border)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {thumb
                  ? <img src={thumb} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top left' }} />
                  : <Puzzle size={20} color="var(--c-text-tertiary)" />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: 0.5 }}>{r.phone || '—'}</span>
                  <StatusBadge status={r.status} />
                </div>
                <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.package_name}{r.seq_no != null ? ` · 第 ${r.seq_no} 单` : ''}{r.nickname ? ` · ${r.nickname}` : ''}
                </div>
                <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 2 }}>
                  {formatBJ(r.confirmed_at || r.created_at)}{r.is_settled ? ' · 已结算' : ''}
                </div>
              </div>
              <ChevronRight size={14} color="var(--c-text-tertiary)" />
            </div>
            {canFix && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 10px 68px', padding: '7px 10px',
                background: 'rgba(255,149,0,0.08)', border: '1px solid rgba(255,149,0,0.25)', borderRadius: 10,
              }}>
                <AlertTriangle size={14} color="var(--c-accent)" style={{ flexShrink: 0 }} />
                <span style={{ flex: 1, minWidth: 0, fontSize: 12, lineHeight: 1.5, color: '#9a6400' }}>
                  {fixFields.length > 0 ? `${fixFields.map(f => FIELD_NAME[f]).join('、')}没有识别出来` : '最终图没有生成成功'}
                </span>
                <button onClick={() => setFixing(r)} style={{
                  flexShrink: 0, padding: '4px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: 'var(--c-accent)', color: '#fff', fontSize: 12, fontWeight: 600,
                }}>{fixFields.length > 0 ? '去补全' : '重新生成'}</button>
              </div>
            )}
          </div>
        );
      })}

      {loading && <p style={{ textAlign: 'center', padding: 12, fontSize: 13, color: 'var(--c-text-secondary)' }}>加载中...</p>}
      {!loading && list.length < total && (
        <button className="btn btn-outline" onClick={() => load(page + 1, true)} style={{ height: 40, marginTop: 8 }}>加载更多（{list.length} / {total}）</button>
      )}

      {viewing && <ImageViewer rec={viewing} onClose={() => setViewing(null)} />}
      {fixing && <PuzzleFixModal rec={fixing} onClose={closeFix} onDone={onFixed} />}
    </div>
  );
}
